import React, { useEffect, useState } from 'react';
import {
  DEFAULT_MEMBER_PROFILE_IMAGE,
  normalizeProfileImageId,
} from '../../utils/classMembersStorage';
import {
  isExternalProfileImageSource,
  parseMemberProfileStorageImageRef,
} from '../../utils/memberProfileDisplay';
import { useMemberPhotoDisplayUrl } from '../../utils/memberPhotoImageCache';
import { getCachedProfileImageUrl } from '../../utils/profileImageCache';

type Props = {
  imageId?: string | null;
  alt?: string;
  className?: string;
  /** 이미지 로드 실패·캐시 실패 시 표시할 폴백(예: 이니셜) */
  fallback?: React.ReactNode;
};

const CachedProfileImage: React.FC<Props> = ({
  imageId,
  alt = '',
  className,
  fallback = null,
}) => {
  const storagePath = parseMemberProfileStorageImageRef(imageId);
  const signedFromStorage = useMemberPhotoDisplayUrl(storagePath);
  const external = isExternalProfileImageSource(imageId);
  const resolvedKey = storagePath
    ? `mpath:${storagePath}`
    : external
      ? String(imageId)
      : normalizeProfileImageId(imageId);
  const [src, setSrc] = useState<string | null>(() => (external ? resolvedKey : null));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    if (storagePath) {
      return () => {
        cancelled = true;
      };
    }
    if (isExternalProfileImageSource(imageId)) {
      setSrc(String(imageId));
      return () => {
        cancelled = true;
      };
    }
    setSrc(null);
    const normalized = normalizeProfileImageId(imageId);
    void getCachedProfileImageUrl(normalized).then((url) => {
      if (cancelled) return;
      if (url) setSrc(url);
      else setFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [imageId, storagePath]);

  const displaySrc = storagePath ? signedFromStorage : src;

  if (failed || !displaySrc) {
    return <>{fallback}</>;
  }

  return (
    <img
      key={resolvedKey}
      className={className}
      src={displaySrc}
      alt={alt}
      draggable={false}
      onError={() => {
        if (storagePath || isExternalProfileImageSource(imageId)) {
          setFailed(true);
          return;
        }
        const id = normalizeProfileImageId(imageId);
        if (id !== DEFAULT_MEMBER_PROFILE_IMAGE) {
          void getCachedProfileImageUrl(DEFAULT_MEMBER_PROFILE_IMAGE).then((url) => {
            if (url) setSrc(url);
            else setFailed(true);
          });
        } else {
          setFailed(true);
        }
      }}
    />
  );
};

export default CachedProfileImage;
